import React, { useEffect, useState } from "react"
import Input from "../Input/Input"
import Dropdown from "../Dropdown/Dropdown"
import { PLAN_LENGTH_MENU } from "../../config/consts"
import { ProgramFee } from "../ProgramFee/ProgramFee"
import { CustodialFee } from "../CustodialFee/CustodialFee"
import { Frequency } from "../Frequency/Frequency"
import { StorageGetItem, USER } from "../../lib/storage"
import FeeShapeChoice from '../FeeShape/FeeShapeChoice'
import { useFlatFeeText } from '../FeeShape/flatFeeInput'
import { flatFeeMenuItems } from '../FeeShape/flatFeeMenu'

// `feeShape` says what the CONTACT'S OFFICE permits: whether a flat fee may be used at all, and
// which amounts are selectable. It carries no amount of its own — that is the rep's, per client.
// Not allowed, or not loaded — which is every office today — and this screen is what it always was.
const SecondStep = ({ calculatedData, setCalculatedData, bankUsersDropdown, userRole, disableInitialFee, feeShape, flatFeeTyped = false, lowerFeeFloor = false }) => {
  const [isAffiliateManager, setIsAffiliateManager] = useState(false)

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setIsAffiliateManager(result?.isAffiliateManager || false)
    })
  }, [])

  useEffect(() => {
    if (disableInitialFee) {
      setCalculatedData((prev) => ({ ...prev, initialFee: 0 }))
    }
  }, [disableInitialFee])

  const handleInputChange = ({ name, value }) => {
    setCalculatedData(prevData => ({ ...prevData, [name]: value }))
  }

  const planMenu = PLAN_LENGTH_MENU({ userRole, isAffiliateManager })

  // The office grants the option; the rep decides for this client. Percentage until they say
  // otherwise, which is what the server defaults to as well — a flat fee is chosen, never
  // inherited from where the client happened to be enrolled.
  const flatAllowed = !!feeShape?.allowed
  const isFlat = flatAllowed && calculatedData?.feeType === 'flat'
  // WS #2346323: the same 15% floor as Add Contact (ProgramPlan.jsx), on the same offices.
  const lowerFloor = lowerFeeFloor && flatAllowed

  const pickShape = (nextType) => {
    setCalculatedData(prev => ({
      ...prev,
      feeType: nextType,
      // A percentage client carries no flat amount, so the two can never disagree about what was
      // agreed. The office holds no figure to start from — the rep names it.
      flatFee: nextType === 'flat' ? (prev?.flatFee ?? null) : null,
    }))
  }

  // The typed flat fee (WS #1944843, flag FLAT_FEE_TYPED_AMOUNT read by CreditReportModal). The plan
  // only ever holds a valid number or null; Calculate refuses null.
  const flatFeeField = useFlatFeeText({
    amount: calculatedData?.flatFee ?? null,
    max: feeShape?.max,
    onAmount: (flatFee) => setCalculatedData(prev => ({ ...prev, flatFee })),
  })

  const flatFeeMenu = flatFeeMenuItems({ options: feeShape?.options, current: calculatedData?.flatFee })

  return (
    <div className="my-6">
      <div className="w-full flex flex-row items-center justify-between gap-5">
        <Dropdown placeholder="Select a Value" label="Program Length:"
          value={calculatedData?.programLength}
          setValue={(e) => handleInputChange({ name: 'programLength', value: e })}
          menuItems={planMenu}
          className="w-full"
        />
        {isFlat && flatFeeTyped ? (
          <Input
            classes="h-[61px]"
            width="w-full"
            parentClassName="w-full"
            placeholder={"8500"}
            label="Flat Fee $:"
            type="text"
            leftSideIcon={<div className="text-text-secondary">$</div>}
            leftSideIconStyle="ml-5 mt-[21px]"
            value={flatFeeField.text}
            onChange={flatFeeField.onChange}
            errorMsg={flatFeeField.error || undefined}
          />
        ) : isFlat ? (
          <Dropdown placeholder="Select a Value" label="Flat Fee:"
            value={calculatedData?.flatFee ?? ''}
            setValue={(e) => handleInputChange({ name: 'flatFee', value: Number(e) })}
            menuItems={flatFeeMenu}
            className="w-full"
          />
        ) : (
          <ProgramFee value={calculatedData?.programFee}
            classes="w-full"
            lowerFloor={lowerFloor}
            setValue={(value) => {
              setCalculatedData(prev => ({ ...prev, programFee: value }))
            }} />
        )}
      </div>

      <FeeShapeChoice allowed={flatAllowed} isFlat={isFlat} onPick={pickShape} />
      <div className="w-full flex flex-row items-center justify-between gap-5 mb-5">
        <CustodialFee value={calculatedData?.custodialFee}
          setValue={(e) => handleInputChange({ name: 'custodialFee', value: e })}
          classes="w-full mt-6" />
        <Dropdown setValue={(e) => handleInputChange({ name: 'billingAccountId', value: e })}
          myListSubheader={[
            { name: 'Bank Accounts', value: 'bankAccounts' },
            { name: 'Credit Cards', value: 'creditCards' }
          ]}
          menuItems={bankUsersDropdown} value={calculatedData['billingAccountId']}
          className="mt-6 w-full" variant="standard" label="Billing Account" />
      </div>
      <Input
        classes="h-[61px]"
        width="w-full"
        placeholder={"5000"}
        label="Enrollment Fee $:"
        leftSideIcon={<div className="text-text-secondary">$</div>}
        leftSideIconStyle="ml-5 mt-[21px]"
        value={disableInitialFee ? 0 : calculatedData?.initialFee}
        onChange={(e) => handleInputChange({ name: 'initialFee', value: e.target.value })}
        disabled={disableInitialFee}
      />
      <Frequency paymentSchedule={calculatedData} setPaymentSchedule={setCalculatedData} userRole={userRole} />
    </div>
  )
}

export default SecondStep
